import { useCallback, useEffect, useRef, useState } from "react";
import { useApiClient } from "../../../apiClient";
import {
    CameraLiveViewStateDto,
    StartCameraLiveViewInputDto,
} from "../../../models/court-recording/dto/venue-agent.dto";
import {
    LIVE_VIEW_MIN_RENEW_DELAY_MS,
    LIVE_VIEW_RENEW_RETRY_MS,
    LIVE_VIEW_REQUEST_TIMEOUT_MS,
    LiveViewPhase,
    classifyRenewal,
    endedByCap,
    liveViewErrorKind,
    nextRenewalDelayMs,
    sameMac,
} from "../utils/camera-aim.utils";

/*
 * Every live-view URL below is written out as a template literal at its
 * `endpoint:` — the shape the API's client-route parity ratchet
 * (`api/src/libs/http/client-route-parity.spec.ts`) can check against the
 * controller. Do not move them into a helper: a path built elsewhere is
 * invisible to it.
 */

/** The box a session runs on: what every live-view URL is built from. */
interface Target {
    agentId: string;
    organizationId: string;
}

const sameTarget = (a: Target, b: Target): boolean =>
    a.agentId === b.agentId && a.organizationId === b.organizationId;

/** True unless the browser says the page is hidden (another tab, screen off, app switched). */
export const isPageVisible = (): boolean =>
    typeof document === 'undefined' || document.visibilityState === 'visible';

/**
 * A live-view request that got no answer in time. It carries `status: 0`,
 * like a dropped connection, so `liveViewErrorKind` reads it as
 * `unavailable` — the one kind the page retries without asking.
 */
export class LiveViewTimeoutError extends Error {
    readonly status = 0;

    constructor(ms: number) {
        super(`No answer within ${ms} ms`);
        this.name = 'LiveViewTimeoutError';
    }
}

/**
 * `promise`, or a `LiveViewTimeoutError` after `ms`, whichever comes first.
 * The request itself goes on (`fetchApi` takes no abort signal); its late
 * answer is simply no longer awaited. `onTimeout` lets a caller that CAN
 * cancel (an `AbortController`) do so.
 */
export function withTimeout<T>(promise: Promise<T>, ms: number, onTimeout?: () => void): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        const timer = setTimeout(() => {
            onTimeout?.();
            reject(new LiveViewTimeoutError(ms));
        }, ms);
        promise.then(
            (value) => {
                clearTimeout(timer);
                resolve(value);
            },
            (error) => {
                clearTimeout(timer);
                reject(error);
            },
        );
    });
}

type ApiClient = ReturnType<typeof useApiClient>;

/** The session this page renews, with everything a renewal or a stop needs. */
interface OwnSession extends Target {
    sessionId: string;
    mac: string;
    /** This page STARTED it (so it ends it when it leaves). A joined or followed session is left running. */
    owned: boolean;
}

/**
 * A DELETE this page owes for a session it started and has left — sent one
 * tick late, on purpose.
 *
 * A camera switch tears the session effect down (`leave`) and sets it up
 * again (`open`) in the same commit, and the START that follows REPLACES the
 * old session on the server by itself (one session per box). Sending the
 * DELETE first would leave the box with no session for the length of that
 * round trip; the box checks about once a second, and once it sees none it
 * stops streaming until its next heartbeat — up to a minute of black picture
 * after every chip tap. So `leave` only parks the stop, and an `open` for the
 * same box that follows at once takes it over (`timer: null`): it drops the
 * stop once its START has replaced the session, and sends it if it did not
 * (refused, joined another session, or superseded in turn — then it parks it
 * again for whoever comes next). Nothing following — an unmount, another
 * box or organization — and the timer sends it.
 */
interface PendingStop extends Target {
    sessionId: string;
    timer: ReturnType<typeof setTimeout> | null;
}

interface Snapshot {
    phase: LiveViewPhase;
    state: CameraLiveViewStateDto | null;
    receivedAt: number | null;
    session: OwnSession | null;
    endedByCap: boolean;
    error: unknown;
}

const IDLE: Snapshot = {
    phase: 'idle',
    state: null,
    receivedAt: null,
    session: null,
    endedByCap: false,
    error: null,
};

/**
 * The timers, listeners and in-flight answers outlive any one render, so the
 * session lives in one mutable object the hook creates once, and React only
 * sees `Snapshot`s published from it.
 *
 * `generation` is the staleness guard: every start, stop, camera switch or
 * unmount bumps it, and an answer that comes back under an older generation
 * is dropped — so a slow renewal can never resurrect a session the page has
 * already left.
 */
interface Machine extends Target {
    client: ApiClient;
    mac: string;
    generation: number;
    session: OwnSession | null;
    state: CameraLiveViewStateDto | null;
    receivedAt: number | null;
    timer: ReturnType<typeof setTimeout> | null;
    /** Generation whose renewal is in flight — at most one at a time. */
    renewing: number | null;
    /** The last start, so the next one waits for it (one POST at a time). */
    opening: Promise<void> | null;
    /** Sessions this page has sent (or parked) a stop for: never joined or followed again. */
    stopped: Set<string>;
    /** See `PendingStop`. */
    pendingStops: PendingStop[];
    /** Unmounted: a stop parked from here on is sent at once, no timer will follow. */
    disposed: boolean;
    publish: (snapshot: Snapshot) => void;
}

function commit(m: Machine, next: Snapshot): void {
    m.session = next.session;
    m.state = next.state;
    m.receivedAt = next.receivedAt;
    m.publish(next);
}

function clearTimer(m: Machine): void {
    if (m.timer !== null) {
        clearTimeout(m.timer);
        m.timer = null;
    }
}

/** Every answer still in flight is stale from here on. */
function invalidate(m: Machine): number {
    clearTimer(m);
    m.generation += 1;
    return m.generation;
}

function schedule(m: Machine, generation: number, delayMs: number): void {
    clearTimer(m);
    m.timer = setTimeout(() => {
        m.timer = null;
        void renew(m, generation);
    }, delayMs);
}

/** A session the box runs on `mac` that this page may watch (it is not ending it). */
function isJoinable(m: Machine, state: CameraLiveViewStateDto | null, mac: string): boolean {
    return Boolean(
        state?.active && state.sessionId && !m.stopped.has(state.sessionId) && sameMac(state.mac, mac),
    );
}

/**
 * `DELETE …/live-view?sessionId=` with `keepalive`, fire-and-forget: from
 * `pagehide` and unmount, where nobody is left to read the answer. Scoped to
 * the session id, so it can never end a session someone else started since.
 */
function sendStop(m: Machine, { agentId, organizationId }: Target, sessionId: string): void {
    m.stopped.add(sessionId);
    m.client
        .fetchRaw({
            endpoint: `court-recordings/admin/venue-agents/${encodeURIComponent(agentId)}/live-view?organizationId=${encodeURIComponent(organizationId)}&sessionId=${encodeURIComponent(sessionId)}`,
            method: 'DELETE',
            keepalive: true,
        })
        .catch(() => undefined);
}

function releaseStop(m: Machine, stop: PendingStop): void {
    const index = m.pendingStops.indexOf(stop);
    if (index < 0) return;
    m.pendingStops.splice(index, 1);
    if (stop.timer !== null) clearTimeout(stop.timer);
    stop.timer = null;
    sendStop(m, stop, stop.sessionId);
}

/** The session was replaced on the server: the stop is owed no more. */
function dropStop(m: Machine, stop: PendingStop): void {
    const index = m.pendingStops.indexOf(stop);
    if (index >= 0) m.pendingStops.splice(index, 1);
}

function armStop(m: Machine, stop: PendingStop): void {
    if (!m.pendingStops.includes(stop)) return;
    if (m.disposed) {
        releaseStop(m, stop);
        return;
    }
    stop.timer = setTimeout(() => {
        stop.timer = null;
        releaseStop(m, stop);
    }, 0);
}

function parkStop(m: Machine, target: Target, sessionId: string): void {
    m.stopped.add(sessionId);
    const stop: PendingStop = { agentId: target.agentId, organizationId: target.organizationId, sessionId, timer: null };
    m.pendingStops.push(stop);
    armStop(m, stop);
}

/** The parked stops for this box, now held by the `open` that asks. */
function holdStops(m: Machine, target: Target): PendingStop[] {
    const held = m.pendingStops.filter((stop) => stop.timer !== null && sameTarget(stop, target));
    held.forEach((stop) => {
        if (stop.timer !== null) clearTimeout(stop.timer);
        stop.timer = null;
    });
    return held;
}

/** `pagehide` and unmount: no later tick is coming, so every stop owed goes now. */
function flushStops(m: Machine): void {
    [...m.pendingStops].forEach((stop) => releaseStop(m, stop));
}

/**
 * One renewal (`POST {mac, sessionId}`), which is also what registers this
 * page as a viewer of that session: the frame relay answers 204 to anyone
 * who has not started or renewed the CURRENT session within its lease.
 */
function postRenewal(client: ApiClient, target: Target, mac: string, sessionId: string): Promise<CameraLiveViewStateDto> {
    return withTimeout(
        client.fetchApi<CameraLiveViewStateDto, StartCameraLiveViewInputDto>({
            endpoint: `court-recordings/admin/venue-agents/${encodeURIComponent(target.agentId)}/live-view?organizationId=${encodeURIComponent(target.organizationId)}`,
            method: 'POST',
            body: { mac, sessionId },
        }),
        LIVE_VIEW_REQUEST_TIMEOUT_MS,
    );
}

/**
 * When to renew a session this page has just started watching: at the normal
 * cadence, except a session it FOLLOWED (a new one on the same camera, which
 * the renewal of the old one did not register it for) — renewed at once, or
 * the relay keeps answering 204 for up to 15 s.
 */
function renewalDelay(answer: CameraLiveViewStateDto, receivedAt: number, followed: boolean): number {
    return followed ? LIVE_VIEW_MIN_RENEW_DELAY_MS : nextRenewalDelayMs(answer, receivedAt, receivedAt);
}

async function renew(m: Machine, generation: number): Promise<void> {
    const own = m.session;
    if (generation !== m.generation || !own) return;
    clearTimer(m);
    // Hidden: no renewal and no timer. The lease lapses by itself within 45 s
    // unless the page comes back first, which renews at once.
    if (!isPageVisible()) return;
    if (m.renewing === generation) return;
    m.renewing = generation;
    try {
        const answer = await postRenewal(m.client, own, own.mac, own.sessionId);
        if (generation !== m.generation) return;
        const receivedAt = Date.now();
        let outcome = classifyRenewal(own, answer);
        // A session this page is ending is about to be gone: never follow it.
        if (outcome === 'follow' && m.stopped.has(answer.sessionId as string)) outcome = 'ended';
        if (outcome === 'renewed' || outcome === 'follow') {
            const session =
                outcome === 'renewed'
                    ? own
                    : { ...own, sessionId: answer.sessionId as string, owned: false };
            commit(m, { phase: 'active', state: answer, receivedAt, session, endedByCap: false, error: null });
            schedule(m, generation, renewalDelay(answer, receivedAt, outcome === 'follow'));
            return;
        }
        // Ended or replaced: the server did NOT renew, and neither does the page.
        commit(m, {
            phase: outcome,
            state: answer,
            receivedAt,
            session: null,
            endedByCap: outcome === 'ended' && endedByCap(m.state, answer),
            error: null,
        });
    } catch (error) {
        if (generation !== m.generation) return;
        if (liveViewErrorKind(error) === 'unavailable') {
            // No answer (4G drop, 5xx, timeout): keep the session and try again soon.
            schedule(m, generation, LIVE_VIEW_RENEW_RETRY_MS);
            return;
        }
        // A refusal (403/404/409): retrying would be refused the same way.
        commit(m, { ...IDLE, phase: 'error', state: m.state, receivedAt: m.receivedAt, error });
    } finally {
        if (m.renewing === generation) m.renewing = null;
    }
}

/** An answer that opens a session for this page: show it and start renewing. */
function adopt(
    m: Machine,
    generation: number,
    target: Target,
    mac: string,
    answer: CameraLiveViewStateDto | null,
    owned: boolean,
    followed: boolean,
): void {
    const receivedAt = Date.now();
    if (!answer?.active || !answer.sessionId) {
        commit(m, { ...IDLE, phase: 'ended', state: answer, receivedAt });
        return;
    }
    commit(m, {
        phase: 'active',
        state: answer,
        receivedAt,
        session: { sessionId: answer.sessionId, mac, ...target, owned },
        endedByCap: false,
        error: null,
    });
    schedule(m, generation, renewalDelay(answer, receivedAt, followed));
}

/**
 * `join-or-start` (on arrival, or a camera switch): if this camera is already
 * being aimed — an operator opening the page a club admin is using — watch
 * that session instead of replacing it, after renewing it once, which is what
 * registers this page as one of its viewers. `start` ("Continuer", "Reprendre
 * la main", "Réessayer"): always a new session, which replaces whatever the
 * box runs.
 */
async function open(
    m: Machine,
    mode: 'join-or-start' | 'start',
    target: Target,
    mac: string,
): Promise<void> {
    const generation = invalidate(m);
    commit(m, { ...IDLE, phase: 'opening' });
    // A camera switch: the stop `leave` just parked for the old session is
    // now this call's to send or drop (see `PendingStop`).
    const held = holdStops(m, target);
    const previous = m.opening;
    const run = (async () => {
        let replacedOnServer = false;
        try {
            // One POST at a time from this page: a camera tapped twice in a row
            // must not let the first start land on the server after the second.
            if (previous) await previous;
            // …and a start that came back after the page moved on parked its
            // stop while this one waited: same box, so it is this call's too.
            held.push(...holdStops(m, target));
            if (generation !== m.generation) return;
            const { client } = m;
            if (mode === 'join-or-start') {
                const current = await withTimeout(
                    client.fetchApi<CameraLiveViewStateDto, {}>({
                        endpoint: `court-recordings/admin/venue-agents/${encodeURIComponent(target.agentId)}/live-view?organizationId=${encodeURIComponent(target.organizationId)}`,
                        method: 'GET',
                    }),
                    LIVE_VIEW_REQUEST_TIMEOUT_MS,
                );
                if (generation !== m.generation) return;
                // Never join a session this page is ending (a start that came
                // back after the page had moved on): its stop is owed.
                if (isJoinable(m, current, mac)) {
                    const joinedId = current.sessionId as string;
                    const renewal = await postRenewal(client, target, mac, joinedId);
                    if (generation !== m.generation) return;
                    const outcome = classifyRenewal({ sessionId: joinedId, mac }, renewal);
                    if (outcome === 'renewed' || (outcome === 'follow' && isJoinable(m, renewal, mac))) {
                        adopt(m, generation, target, mac, renewal, false, outcome === 'follow');
                        return;
                    }
                    // It ended (or moved to another camera) between the two
                    // calls: start one, as if it had never been there.
                }
            }
            const request = client.fetchApi<CameraLiveViewStateDto, StartCameraLiveViewInputDto>({
                endpoint: `court-recordings/admin/venue-agents/${encodeURIComponent(target.agentId)}/live-view?organizationId=${encodeURIComponent(target.organizationId)}`,
                method: 'POST',
                body: { mac },
            });
            let answer: CameraLiveViewStateDto;
            try {
                answer = await withTimeout(request, LIVE_VIEW_REQUEST_TIMEOUT_MS);
            } catch (error) {
                if (error instanceof LiveViewTimeoutError) {
                    // The server may still start it. If the page is still
                    // waiting on this very start (no "Réessayer", no switch),
                    // the late answer is simply the session; otherwise nobody
                    // wants it, and it is ended rather than left to its lease.
                    request.then(
                        (late) => {
                            if (generation === m.generation) adopt(m, generation, target, mac, late, true, false);
                            else if (late?.sessionId) parkStop(m, target, late.sessionId);
                        },
                        () => undefined,
                    );
                }
                throw error;
            }
            replacedOnServer = Boolean(answer?.active && answer.sessionId);
            if (generation !== m.generation) {
                // The page left (or switched camera) while this was starting.
                if (answer?.sessionId) parkStop(m, target, answer.sessionId);
                return;
            }
            adopt(m, generation, target, mac, answer, true, false);
        } catch (error) {
            if (generation !== m.generation) return;
            commit(m, { ...IDLE, phase: 'error', error });
        } finally {
            held.forEach((stop) => {
                if (replacedOnServer) dropStop(m, stop);
                else if (generation !== m.generation) armStop(m, stop);
                else releaseStop(m, stop);
            });
        }
    })();
    m.opening = run;
    await run;
}

/**
 * Unmount, or a different camera/agent: end our own session, quietly — one
 * tick late, so a camera switch's START can replace it instead (`PendingStop`).
 */
function leave(m: Machine): void {
    const own = m.session;
    invalidate(m);
    commit(m, IDLE);
    if (own?.owned) parkStop(m, own, own.sessionId);
}

/**
 * "Terminer". Ends the session this page started; a JOINED one (an operator
 * watching the club admin's session) is only left — ending it would cut the
 * admin's picture off mid-aim — and its lease lapses within 45 s if nobody
 * else renews it.
 */
async function stopSession(m: Machine): Promise<void> {
    const own = m.session;
    invalidate(m);
    commit(m, { ...IDLE, phase: 'ended', state: m.state, receivedAt: m.receivedAt });
    if (!own?.owned) return;
    m.stopped.add(own.sessionId);
    try {
        await withTimeout(
            m.client.fetchApi<CameraLiveViewStateDto, {}>({
                endpoint: `court-recordings/admin/venue-agents/${encodeURIComponent(own.agentId)}/live-view?organizationId=${encodeURIComponent(own.organizationId)}&sessionId=${encodeURIComponent(own.sessionId)}`,
                method: 'DELETE',
            }),
            LIVE_VIEW_REQUEST_TIMEOUT_MS,
        );
    } catch {
        // Nothing to show for it: the lease lapses within 45 s anyway.
    }
}

/**
 * `pagehide` (tab closed, navigated away, put in the back-forward cache): end
 * our own session with a `keepalive` request, the only kind that survives,
 * and send every stop still parked — no timer runs after this. A JOINED
 * session is left running, as on "Terminer". If the page is restored, it
 * shows "Session terminée" and "Continuer".
 */
function onPageHide(m: Machine): void {
    flushStops(m);
    const own = m.session;
    if (!own?.owned) return;
    invalidate(m);
    commit(m, { ...IDLE, phase: 'ended', state: m.state, receivedAt: m.receivedAt });
    sendStop(m, own, own.sessionId);
}

export interface CameraLiveViewSession {
    phase: LiveViewPhase;
    /** The API's last answer about this box's session. */
    state: CameraLiveViewStateDto | null;
    /** When `state` arrived, on this page's clock — to age API timestamps without trusting the phone's clock. */
    receivedAt: number | null;
    /** The session this page renews; `null` when none. */
    sessionId: string | null;
    /** This page started `sessionId`, and ends it when it leaves. */
    owned: boolean;
    /** Ended or replaced: nothing is renewed until `start()`. */
    ended: boolean;
    /** The session ended at its 15-minute cap (only to word the message). */
    endedByCap: boolean;
    /** Set in the `error` phase: the API's refusal (400/403/404/409), or `unavailable`. */
    error: unknown;
    /** Start a NEW session on this camera ("Continuer", "Reprendre la main"). */
    start: () => Promise<void>;
    /** "Terminer": end the session this page started (a joined one is only left). Resolves once the API answered, failed to, or timed out. */
    stop: () => Promise<void>;
    /** Renew now instead of at the next tick — the frame relay hinted the session changed. */
    checkNow: () => void;
}

/**
 * The page's aim-view session on one camera (docs/epics/camera-aim-live-view.md).
 *
 * The session is a 45-second lease the page keeps alive:
 * - on mount it joins the box's running session if it is on this camera —
 *   renewing it once first, which registers this page as a viewer (the frame
 *   relay serves only registered viewers) — else starts one (which replaces
 *   any other camera's session on the box);
 * - it renews every 15 s, ONLY while the page is visible, and renews at once
 *   when the page becomes visible again — a hidden tab costs the box nothing
 *   after 45 s;
 * - it stops renewing as soon as the API answers with no session or another
 *   one (`classifyRenewal`), so the 15-minute cap holds: only `start()`
 *   opens a new session;
 * - every request gives up after `LIVE_VIEW_REQUEST_TIMEOUT_MS`, so a hung one
 *   cannot block the renewals behind it;
 * - on unmount and on `pagehide` it ends the session it started, with a
 *   `keepalive` DELETE; a camera switch lets the new START replace it.
 *
 * Not React Query: a lease is a timer with side effects on leave, not a cached
 * read, and a second consumer sharing a cache entry would double-renew.
 */
export function useCameraLiveViewSession({
    agentId,
    organizationId,
    mac,
    enabled = true,
}: {
    agentId: string;
    organizationId: string;
    /** The camera, as the API takes it (`parseAimMac`). Empty = nothing to aim. */
    mac: string;
    enabled?: boolean;
}): CameraLiveViewSession {
    const client = useApiClient();
    const [snapshot, setSnapshot] = useState<Snapshot>(IDLE);
    const machineRef = useRef<Machine | null>(null);
    if (machineRef.current === null) {
        machineRef.current = {
            client,
            agentId: '',
            organizationId: '',
            mac: '',
            generation: 0,
            session: null,
            state: null,
            receivedAt: null,
            timer: null,
            renewing: null,
            opening: null,
            stopped: new Set<string>(),
            pendingStops: [],
            disposed: false,
            publish: setSnapshot,
        };
    }
    const m = machineRef.current;
    // Latest values for `start()`; a running session keeps the ones it began with.
    m.client = client;
    m.agentId = agentId;
    m.organizationId = organizationId;
    m.mac = mac;

    useEffect(() => {
        if (!enabled || !agentId || !organizationId || !mac) return undefined;
        void open(m, 'join-or-start', { agentId, organizationId }, mac);
        return () => leave(m);
    }, [m, enabled, agentId, organizationId, mac]);

    useEffect(() => {
        m.disposed = false;
        const handleVisibility = (): void => {
            if (isPageVisible() && m.session) void renew(m, m.generation);
        };
        const handlePageHide = (): void => onPageHide(m);
        document.addEventListener('visibilitychange', handleVisibility);
        window.addEventListener('pagehide', handlePageHide);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibility);
            window.removeEventListener('pagehide', handlePageHide);
            // Unmounted: the stop `leave` just parked goes now, not a tick later.
            m.disposed = true;
            flushStops(m);
        };
    }, [m]);

    const start = useCallback(
        () => open(m, 'start', { agentId: m.agentId, organizationId: m.organizationId }, m.mac),
        [m],
    );
    const stop = useCallback(() => stopSession(m), [m]);
    const checkNow = useCallback(() => {
        if (m.session) void renew(m, m.generation);
    }, [m]);

    return {
        phase: snapshot.phase,
        state: snapshot.state,
        receivedAt: snapshot.receivedAt,
        sessionId: snapshot.session?.sessionId ?? null,
        owned: snapshot.session?.owned ?? false,
        ended: snapshot.phase === 'ended' || snapshot.phase === 'replaced',
        endedByCap: snapshot.endedByCap,
        error: snapshot.error,
        start,
        stop,
        checkNow,
    };
}
